import type { TFunction } from 'i18next';
import { formatClock } from '@/features/trips/format';
import { formatDay, formatDays, formatEngineHours, formatKm } from './format';
import type { MaintenanceAlert } from './types';

/**
 * A maintenance reminder in the alerts feed (slice 28).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔴 THIS ROW DEEP-LINKS TO THE VEHICLE'S MAINTENANCE TAB, NEVER TO A MAP.
 * ═════════════════════════════════════════════════════════════════════════════
 * A service falling due did not happen ANYWHERE. It has no location, the wire
 * item carries no `observedLat`/`observedLon`, and it must never be given one.
 *
 * DO NOT "FIX THE INCONSISTENCY" by attaching `pos:{deviceId}` or the vehicle's
 * last-known position so this row behaves like the other two. That would put live
 * state where an observed fact belongs — the exact trap ADR 0011 decision 2 names,
 * and the same shape as position 27, where one invented coordinate was believed by
 * every layer downstream and could not be cleaned up afterwards. The other feed
 * rows centre a map on a crossing's OWN frozen coordinates; this one has none to
 * freeze, and the honest rendering of "no location" is no map.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔴 EVERY DISPLAYED VALUE COMES FROM THE ALERT'S FROZEN `detail`, NEVER A LIVE
 * JOIN TO `schedules`.
 * ═════════════════════════════════════════════════════════════════════════════
 * `scheduleTitle`, `trigger`, `remaining`, `dueValue` and `dueOn` are projected
 * from what was true when the reminder fired. The title may have been edited
 * since, and the schedule may have been completed and re-armed — a live join
 * would silently rewrite history so that a row said something the customer was
 * never told. The wire ships stored units (metres / seconds / days), so the
 * formatting happens once here through the shared formatters and no `km` string
 * is hardcoded.
 *
 * Both severities are alert-class (`warning` = due, `critical` = overdue), so both
 * appear under the feed's `Alerts` chip. The chips themselves stay `All / Alerts /
 * Unread` — frozen vocabulary, no new filter.
 */

/** Stored units → the sentence. `remaining` is negative when overdue. */
function line(t: TFunction, locale: string, a: MaintenanceAlert): string {
  const overdue = a.type === 'maintenance_overdue';
  if (a.remaining === null) {
    return overdue
      ? t('alerts.maintenance.overduePlain', { title: a.scheduleTitle })
      : t('alerts.maintenance.duePlain', { title: a.scheduleTitle });
  }
  const magnitude =
    a.trigger === 'every_km'
      ? formatKm(t, locale, Math.abs(a.remaining) / 1000)
      : a.trigger === 'every_hours'
        ? formatEngineHours(t, Math.abs(a.remaining) / 3600)
        : formatDays(t, a.remaining);
  return overdue
    ? t('alerts.maintenance.overdueBy', { title: a.scheduleTitle, value: magnitude })
    : t('alerts.maintenance.dueIn', { title: a.scheduleTitle, value: magnitude });
}

export function MaintenanceRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: MaintenanceAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: MaintenanceAlert) => void;
  t: TFunction;
}) {
  const critical = alert.type === 'maintenance_overdue';
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const titleClass = !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(alert)}
        aria-pressed={selected}
        data-severity={alert.severity}
        data-alert-id={alert.id}
        data-testid="maintenance-alert-row"
        className={[
          'w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span
            aria-hidden="true"
            className={[
              'mt-1.5 size-2 flex-none rounded-full',
              alert.read ? 'bg-transparent' : 'bg-raad-accent',
            ].join(' ')}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>
                {line(t, locale, alert)}
              </span>
              <span
                data-testid="alert-tag"
                className={[
                  'flex-none rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  critical ? 'bg-raad-error/15 text-raad-error' : 'bg-raad-warn/18 text-raad-warn',
                ].join(' ')}
              >
                {t(critical ? 'alerts.type.maintenanceOverdue' : 'alerts.type.maintenanceDue')}
              </span>
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}

            <div className="mt-1 text-[12px] text-raad-muted">
              {alert.dueOn !== null
                ? t('alerts.maintenance.dueOnMeta', { date: formatDay(locale, alert.dueOn) })
                : t('alerts.observedAt', { time: observedTime })}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}
